import { orderBlockHints } from './orderBlocks.js';
import { bookFromDensity, clampDensity } from './strategyTuning.js';
import { inTradeRange } from './types.js';
/** Distance from close to zone (0 = inside). Positive = away. */
export const distToZonePct = (close, z) => {
    if (!(close > 0))
        return Infinity;
    if (close >= z.priceLow && close <= z.priceHigh)
        return 0;
    if (close < z.priceLow)
        return ((z.priceLow - close) / close) * 100;
    return ((close - z.priceHigh) / close) * 100;
};
const okStatus = (s, allowTested) => {
    if (s === 'mitigated')
        return false;
    if (s === 'tested')
        return allowTested;
    return true;
};
/**
 * Pick best qualifying OB for a side.
 * Match: close inside, close within approachPct, or a wick tap whose close
 * is still near the brick (naked far wicks must not steal the side).
 */
export const pickBookZone = (zones, side, bar, minStrength, allowTested, approachPct = 0) => {
    let best = null;
    // Wick-only ceiling: ~1.5× approach, floor 0.2% so sparse dens still filters far tags.
    const wickMaxPct = Math.max(approachPct > 0 ? approachPct * 1.5 : 0.2, 0.2);
    for (const z of zones) {
        if (z.side !== side)
            continue;
        if (!okStatus(z.status, allowTested))
            continue;
        if ((z.strength ?? 0) < minStrength)
            continue;
        const dist = distToZonePct(bar.close, z);
        const closeIn = bar.close >= z.priceLow && bar.close <= z.priceHigh;
        const near = approachPct > 0 && dist <= approachPct;
        const wickOverlap = bar.low <= z.priceHigh && bar.high >= z.priceLow;
        const wickTap = wickOverlap && dist <= wickMaxPct;
        if (!closeIn && !wickTap && !near)
            continue;
        // Demand is bought from above; supply sold from below. Reject deep wrong-side closes
        // that only nick the brick with a wick (mitigated bars are already filtered upstream).
        if (side === 'bull' && bar.close < z.priceLow && !near && !closeIn)
            continue;
        if (side === 'bear' && bar.close > z.priceHigh && !near && !closeIn)
            continue;
        if (!best || (z.strength ?? 0) > (best.strength ?? 0))
            best = z;
    }
    return best;
};
/**
 * Resolve bull vs bear when both qualify.
 * Prefer the nearer brick; strength only as a tie-break when distances are close.
 * Fixes “sitting under red supply → long a distant green wick” mis-entries.
 */
export const resolveBookSidePick = (bull, bear, close) => {
    if (bull && !bear)
        return { zone: bull, side: 'long' };
    if (bear && !bull)
        return { zone: bear, side: 'short' };
    if (!bull || !bear)
        return null;
    const db = distToZonePct(close, bull);
    const ds = distToZonePct(close, bear);
    // Clearer proximity wins (15% relative gap, min 0.05 abs %).
    const gap = Math.abs(db - ds);
    const rel = Math.max(db, ds, 1e-9);
    if (gap >= 0.05 && gap / rel >= 0.15) {
        return db < ds ? { zone: bull, side: 'long' } : { zone: bear, side: 'short' };
    }
    const zone = (bull.strength ?? 0) >= (bear.strength ?? 0) ? bull : bear;
    return { zone, side: zone === bull ? 'long' : 'short' };
};
export const bookZonesAtIndex = (candles, index, params = {}) => {
    const slice = candles.slice(0, index + 1);
    const longD = bookFromDensity('long', clampDensity(params.longTradeDensity, 55));
    const shortD = bookFromDensity('short', clampDensity(params.shortTradeDensity, 40));
    const dens = Math.max(longD.sensitivity, shortD.sensitivity);
    const sens = Number(params.hintSensitivity ?? dens);
    const maxBlocks = Math.max(1, Number(params.maxBlocks ?? Math.max(longD.maxBlocks, shortD.maxBlocks)));
    return orderBlockHints(slice, maxBlocks, sens, null, 'annotate');
};
/** Desk/engine helper: what Book is looking at on the last closed bar. */
export const bookWatchSnapshot = (candles, index, params = {}) => {
    const c = candles[index];
    if (!c)
        return { mode: 'none', freshCount: 0, testedCount: 0 };
    const longOn = params.longEnabled !== false;
    const shortOn = params.shortEnabled !== false;
    const longD = bookFromDensity('long', clampDensity(params.longTradeDensity, 55));
    const shortD = bookFromDensity('short', clampDensity(params.shortTradeDensity, 40));
    const zones = bookZonesAtIndex(candles, index, params);
    const freshCount = zones.filter((z) => z.status === 'fresh').length;
    const testedCount = zones.filter((z) => z.status === 'tested').length;
    if (!inTradeRange(c.close, c.time, params.range)) {
        return { mode: 'none', freshCount, testedCount };
    }
    const bar = { low: c.low, high: c.high, close: c.close };
    const minLong = Number(params.minStrength ?? longD.minStrength);
    const minShort = Number(params.minStrength ?? shortD.minStrength);
    const allowLong = params.allowTested != null ? Boolean(params.allowTested) : longD.allowTested;
    const allowShort = params.allowTested != null ? Boolean(params.allowTested) : shortD.allowTested;
    const bull = longOn
        ? pickBookZone(zones, 'bull', bar, minLong, allowLong, longD.approachPct)
        : null;
    const bear = shortOn
        ? pickBookZone(zones, 'bear', bar, minShort, allowShort, shortD.approachPct)
        : null;
    const picked = resolveBookSidePick(bull, bear, c.close);
    if (picked) {
        const hit = picked.zone;
        return {
            mode: 'enter',
            side: picked.side,
            strength: hit.strength,
            priceLow: hit.priceLow,
            priceHigh: hit.priceHigh,
            distPct: distToZonePct(c.close, hit),
            status: hit.status,
            freshCount,
            testedCount,
        };
    }
    // Nearest qualifying brick we are waiting on
    let nearest = null;
    for (const z of zones) {
        if (z.side === 'bull' && !longOn)
            continue;
        if (z.side === 'bear' && !shortOn)
            continue;
        const minS = z.side === 'bull' ? minLong : minShort;
        const allow = z.side === 'bull' ? allowLong : allowShort;
        if (!okStatus(z.status, allow))
            continue;
        if ((z.strength ?? 0) < minS)
            continue;
        const d = distToZonePct(c.close, z);
        const sideZ = z.side === 'bull' ? 'long' : 'short';
        if (!nearest || d < nearest.d)
            nearest = { z, side: sideZ, d };
    }
    if (nearest) {
        return {
            mode: 'wait',
            side: nearest.side,
            strength: nearest.z.strength,
            priceLow: nearest.z.priceLow,
            priceHigh: nearest.z.priceHigh,
            distPct: nearest.d,
            status: nearest.z.status,
            freshCount,
            testedCount,
        };
    }
    return { mode: 'none', freshCount, testedCount };
};
/** Soft exit: open long taps a bear brick (or short taps bull). */
export const bookOpposingTap = (side, candles, index, params = {}) => {
    const c = candles[index];
    if (!c)
        return false;
    const densSide = side === 'long' ? 'short' : 'long';
    const dens = bookFromDensity(densSide, clampDensity(side === 'long' ? params.shortTradeDensity : params.longTradeDensity, side === 'long' ? 40 : 55));
    const zones = bookZonesAtIndex(candles, index, params);
    const bar = { low: c.low, high: c.high, close: c.close };
    const want = side === 'long' ? 'bear' : 'bull';
    const minS = Number(params.minStrength ?? dens.minStrength);
    const allow = params.allowTested != null ? Boolean(params.allowTested) : dens.allowTested;
    return pickBookZone(zones, want, bar, minS, allow, dens.approachPct) != null;
};
